import {
  HQ_SSE_EVENTS,
  SSE_EVENTS,
  type HqSseEventName,
  type SseEventName,
  type SseEventPayload,
} from '@missions/types';
import type { z } from 'zod';
import type { ApiClient } from './client';

/* ============================================================================
   SSE subscription with auto-reconnect (exponential backoff, max 30s) and a
   polling fallback hook. Payloads are validated with the contract schemas.
   ========================================================================== */

export type SseHandlers = { [E in SseEventName]?: (payload: SseEventPayload<E>) => void };
export type HqSseHandlers = { [E in HqSseEventName]?: (payload: z.infer<(typeof HQ_SSE_EVENTS)[E]>) => void };

export interface StreamHandle {
  close(): void;
}

interface Opts {
  onOpen?: () => void;
  /** called after each failed attempt: use it to poll counts while disconnected */
  onDisconnect?: (attempt: number) => void;
  EventSourceImpl?: typeof EventSource;
}

function connect(
  api: ApiClient,
  key: 'stream.tenant' | 'stream.hq',
  schemas: Record<string, z.ZodTypeAny>,
  handlers: Record<string, ((p: unknown) => void) | undefined>,
  opts: Opts,
): StreamHandle {
  const ES = opts.EventSourceImpl ?? (typeof EventSource !== 'undefined' ? EventSource : undefined);
  let es: EventSource | null = null;
  let closed = false;
  let attempt = 0;
  let timer: ReturnType<typeof setTimeout> | undefined;

  const open = (): void => {
    if (closed || !ES) return;
    const token = api.getAccessToken();
    es = new ES(api.url(key, { query: token ? { access_token: token } : {} }), { withCredentials: true });
    es.onopen = () => {
      attempt = 0;
      opts.onOpen?.();
    };
    es.onerror = () => {
      es?.close();
      attempt += 1;
      opts.onDisconnect?.(attempt);
      const delay = Math.min(30_000, 1000 * 2 ** Math.min(attempt, 5));
      // refresh the token before reconnecting (it may have expired)
      timer = setTimeout(() => {
        void api.refresh().finally(open);
      }, delay);
    };
    for (const name of Object.keys(schemas)) {
      es.addEventListener(name, (ev) => {
        const h = handlers[name];
        if (!h) return;
        try {
          const parsed = schemas[name]?.safeParse(JSON.parse((ev as MessageEvent<string>).data));
          if (parsed?.success) h(parsed.data);
          else console.warn(`[sse] ${name} payload does not match the contract`);
        } catch {
          /* ignore malformed */
        }
      });
    }
  };

  open();
  return {
    close() {
      closed = true;
      if (timer) clearTimeout(timer);
      es?.close();
    },
  };
}

/** Tenant stream: notifications, live mission updates, release.published (VersionGate). */
export const openTenantStream = (api: ApiClient, handlers: SseHandlers, opts: Opts = {}): StreamHandle =>
  connect(api, 'stream.tenant', SSE_EVENTS, handlers as Record<string, ((p: unknown) => void) | undefined>, opts);

/** Mission HQ stream. */
export const openHqStream = (api: ApiClient, handlers: HqSseHandlers, opts: Opts = {}): StreamHandle =>
  connect(api, 'stream.hq', HQ_SSE_EVENTS, handlers as Record<string, ((p: unknown) => void) | undefined>, opts);
